import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const FrontendHeader: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  // Function to close mobile menu
  const closeMobileMenu = () => {
    // Get the offcanvas element
    const offcanvasElement = document.getElementById("menu-mobile");
    if (offcanvasElement) {
      // Use Bootstrap's offcanvas hide method
      const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(
        offcanvasElement
      );
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      } else {
        // Fallback method - trigger the close button
        const closeButton = offcanvasElement.querySelector(
          '[data-bs-dismiss="offcanvas"]'
        ) as HTMLElement;
        if (closeButton) {
          closeButton.click();
        }
      }
    }
  };

  // Check if user is logged in on component mount and when localStorage changes
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem("frontendUserToken");
      const name = localStorage.getItem("frontendUserName");

      if (token && name) {
        setIsLoggedIn(true);
        setUserName(name);
      } else {
        setIsLoggedIn(false);
        setUserName("");
      }
    };

    checkLoginStatus();

    // Listen for storage changes (in case user logs in/out in another tab)
    const handleStorageChange = () => {
      checkLoginStatus();
    };

    window.addEventListener("storage", handleStorageChange);

    // Custom event listener for login/logout actions in the same tab
    window.addEventListener("loginStatusChanged", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("loginStatusChanged", handleStorageChange);
    };
  }, []);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownActive(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDropdownToggle = () => {
    setIsDropdownActive(!isDropdownActive);
  };

  const handleLogout = () => {
    // Clear all session storage items
    localStorage.removeItem("frontendUserToken");
    localStorage.removeItem("frontendUserId");
    localStorage.removeItem("frontendUserType");
    localStorage.removeItem("frontendUserName");
    localStorage.removeItem("frontendUserEmail");

    // Update state
    setIsLoggedIn(false);
    setUserName("");
    setIsDropdownActive(false);

    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event("loginStatusChanged"));

    // Close mobile menu if open
    closeMobileMenu();

    // Navigate to home page
    navigate("/");
  };

  const handleLoginClick = () => {
    closeMobileMenu();
    navigate("/authuser/login");
  };

  // Handle mobile navigation click
  const handleMobileNavClick = (path: string) => {
    closeMobileMenu();
    // Small delay to ensure menu closes before navigation
    setTimeout(() => {
      navigate(path);
    }, 100);
  };

  return (
    <header id="header-main" className="header">
      <div className="header-inner">
        <div className="tf-container">
          <div className="row">
            <div className="col-12">
              <div className="header-inner-wrap">
                <div className="header-logo">
                  <Link to="/" className="site-logo">
                    <img
                      className="logo_header"
                      alt=""
                      src={`${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/logo.png`}
                    />
                  </Link>
                </div>
                <div className="header-right">
                  <nav className="main-menu">
                    <ul className="navigation">
                      <li className={isActive("/") ? "current-menu" : ""}>
                        <Link to="/">Home</Link>
                      </li>
                      <li
                        className={isActive("/about-us") ? "current-menu" : ""}
                      >
                        <Link to="/about-us">About Us</Link>
                      </li>
                      <li className={isActive("/lands") ? "current-menu" : ""}>
                        <Link to="/lands">For Buyers</Link>
                      </li>
                      <li className={isActive("/authuser/login") ? "current-menu" : ""}>
                        <Link to="/authuser/login">For Sellers</Link>
                      </li>
                      {/* <li
                        className={
                          isActive("/for-buyers") ? "current-menu" : ""
                        }
                      >
                        <Link to="/for-buyers">For Buyers</Link>
                      </li> */}
                      {/* <li
                        className={
                          isActive("/for-sellers") ? "current-menu" : ""
                        }
                      >
                        <Link to="/for-sellers">For Sellers</Link>
                      </li> */}
                      <li className={isActive("/blogs") ? "current-menu" : ""}>
                        <Link to="/blogs">Blog</Link>
                      </li>
                      <li
                        className={
                          isActive("/contact-us") ? "current-menu" : ""
                        }
                      >
                        <Link to="/contact-us">Contact</Link>
                      </li>
                    </ul>
                  </nav>
                  {/* Conditional rendering based on login status */}
                  {isLoggedIn ? (
                    <div
                      className={`box-user tf-action-btns ${
                        isDropdownActive ? "active" : ""
                      }`}
                      onClick={handleDropdownToggle}
                      ref={dropdownRef}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="bi bi-person"></i>
                      <div className="name" title={userName}>
                        {(() => {
                          if (userName.length <= 10) return userName;

                          const slice = userName.slice(0, 10);
                          const lastSpace = slice.lastIndexOf(" ");
                          const result =
                            lastSpace > -1 ? slice.slice(0, lastSpace) : slice;

                          return result + "…";
                        })()}
                        <i className="icon-CaretDown"></i>
                      </div>

                      <div className="menu-user">
                        <Link
                          className="dropdown-item"
                          to="/authuser/profile"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="bi bi-person-gear"></i>
                          My profile
                        </Link>
                        <a
                          className="dropdown-item"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleLogout();
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-box-arrow-right"></i>
                          Logout
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="btn-add">
                      <button
                        className="tf-btn style-border pd-23"
                        onClick={handleLoginClick}
                        style={{ border: "none", background: "transparent" }}
                      >
                        <i className="bi bi-person"></i>
                        Explore/Post Land
                        <div className="fre__outerwrap">FREE</div>
                      </button>
                    </div>
                  )}
                  <div
                    className="mobile-button"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#menu-mobile"
                    aria-controls="menu-mobile"
                  >
                    <i className="icon-menu"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className="offcanvas offcanvas-start mobile-nav-wrap"
        tabIndex={-1}
        id="menu-mobile"
        aria-labelledby="menu-mobile"
      >
        <div className="offcanvas-header top-nav-mobile">
          <div className="offcanvas-title">
            <a href="index.html">
              <img src="images/logo.png" alt="" />
            </a>
          </div>
          <div data-bs-dismiss="offcanvas" aria-label="Close">
            <i className="icon-close"></i>
          </div>
        </div>
        <div className="offcanvas-body inner-mobile-nav">
          <div className="mb-body">
            <ul id="menu-mobile-menu">
              <li className="menu-item">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMobileNavClick("/");
                  }}
                  className="tem-menu-mobile"
                >
                  Home
                </a>
              </li>
              <li className="menu-item">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMobileNavClick("/about-us");
                  }}
                  className="tem-menu-mobile"
                >
                  About Us
                </a>
              </li>
              <li className="menu-item">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMobileNavClick("/lands");
                  }}
                  className="tem-menu-mobile"
                >
                  For Buyers
                </a>
              </li>
               <li className="menu-item">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMobileNavClick("/authuser/login");
                  }}
                  className="tem-menu-mobile"
                >
                  For Sellers
                </a>
              </li>
              <li className="menu-item">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMobileNavClick("/blogs");
                  }}
                  className="tem-menu-mobile"
                >
                  Blog
                </a>
              </li>
              <li className="menu-item">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMobileNavClick("/contact-us");
                  }}
                  className="tem-menu-mobile"
                >
                  Contact
                </a>
              </li>
              {/* Mobile menu conditional rendering */}
              {isLoggedIn ? (
                <>
                  <li className="menu-item">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleMobileNavClick("/authuser/profile");
                      }}
                      className="tem-menu-mobile"
                    >
                      <i className="bi bi-person-gear me-1"></i>
                      My Profile
                    </a>
                  </li>
                  <li className="menu-item">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleLogout();
                      }}
                      className="tem-menu-mobile"
                    >
                      <i className="bi bi-box-arrow-right me-1"></i>
                      Logout
                    </a>
                  </li>
                </>
              ) : (
                <li className="menu-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLoginClick();
                    }}
                    className="tem-menu-mobile"
                    style={{
                      border: "none",
                      background: "none",
                      textAlign: "left",
                      width: "100%",
                    }}
                  >
                    <i className="bi bi-person me-1"></i>
                    Explore/Post Land
                    <div className="fre__outerwrap">FREE</div>
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </header>
  );
};

export default FrontendHeader;
